import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { configuration as analyticsConfig } from '@core/services/core/analytics/configuration';
import { NotificationsCoreService } from '@core/services/core/notifications/notifications-core.service';
import { OpinatorService } from '@core/services/core/opinator.service';
import { TrackingService } from '@core/services/core/user-tracking.service';
import { VehicleAccidentsHelperService } from '@core/services/core/vehicle-accidents-helper.service';
import { FirstRunHelperService } from '@core/services/helpers/first-run-helper.service';
import { GuidedLoginHelperService } from '@core/services/helpers/guided-login-helper.service';
import { LoginHelper } from '@core/services/helpers/login-helper.service';
import { Badge } from '@ionic-native/badge/ngx';
import { Keyboard } from '@ionic-native/keyboard/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { Platform } from '@ionic/angular';
import { AllFeatureFlags } from '@models/mmobile';
import { LoginRouting } from '@modules/login/models';
import { RegisterRouting } from '@modules/register/models/register';
import { TranslateService } from '@ngx-translate/core';
import { LockFontSizePlugin } from 'cordova-plugin-lock-font-size/types/index';
import * as moment from 'moment';
import { merge, of } from 'rxjs';
import {
  delay,
  distinctUntilKeyChanged,
  filter,
  first,
  map,
  share, switchMap
} from 'rxjs/operators';
import {
  Environment,
  HardwareBackButton,
  MMobileService,
  SentryErrorHandler
} from './core/services/common';
import { AnalyticsInitializer } from './core/services/core/analytics/analytics-initializer';
import { Logout } from './core/services/core/auth/auth.actions';
import { AuthService } from './core/services/core/auth/auth.service';
import { BiometricService } from './core/services/core/auth/biometric.service';
import { ContextService } from './core/services/core/context.service';
import { DeeplinkService } from './core/services/core/deeplink.service';
import { LogService } from './core/services/core/log.service';
import { MenuService } from './core/services/core/menu.service';
import { MMobileHelperService } from './core/services/core/mmobile-helper.service';
import { NavigationHelper } from './core/services/core/navigation-helper.service';
import { SplashscreenService } from './core/services/core/splashscreen.service';
import { UiHelperService } from './core/services/core/ui-helper.service';
import { UpdateService } from './core/services/core/update.service';
import { AppBlockGuard } from './core/services/guards/app/app-block-guard';
import { FileHelperService } from './core/services/helpers/file-helper.service';
import { MenuComponent } from './shared/components/menu/menu.component';

declare const LockFontSize: LockFontSizePlugin;
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent implements OnInit {
  menuBackground = false;
  swipeMenuEnabled = true;
  @ViewChild('customMenu', { static: true }) menuRef: MenuComponent;

  constructor(
    public auth: AuthService,
    public ctx: ContextService,
    public menu: MenuService,
    private platform: Platform,
    private statusBar: StatusBar,
    private keyboard: Keyboard,
    private mmobile: MMobileService,
    private trans: TranslateService,
    private log: LogService,
    private ui: UiHelperService,
    private nav: NavigationHelper,
    private hardwareBackButton: HardwareBackButton,
    private analytics: AnalyticsInitializer,
    private splash: SplashscreenService,
    private deeplinks: DeeplinkService,
    private appBlockGuard: AppBlockGuard,
    private fileHelper: FileHelperService,
    private biometric: BiometricService,
    private badge: Badge,
    private router: Router,
    private updateService: UpdateService,
    private vehicleAccidentsHelper: VehicleAccidentsHelperService,
    private loginHelper: LoginHelper,
    private mmobileHelper: MMobileHelperService,
    private opinatorService: OpinatorService,
    private notificationsCoreService: NotificationsCoreService,
    private sentry: SentryErrorHandler,
    private firstRunService: FirstRunHelperService,
    private guidedLoginHelper: GuidedLoginHelperService,
    private trackingService: TrackingService
  ) { }

  @HostListener('window:popstate', ['$event'])
  onPopState(_) {
    if (!this.platform.is('cordova')) {
      setTimeout(() => {
        this.loginHelper.afterLoginNavigation();
      }, 0);
    }
  }

  async ngOnInit() {
    await this.platform.ready();
    this.log.d(this, 'Initialization: platform is ready!');
    this.log.i(this, 'Version:', Environment.config().version);
    this.initializeCommon();
    this.initializeApp();
    this.setupVehicleAccidentsAccess();
  }

  private async initializeCommon() {
    this.firstRunService.isFirstAppRun().then(isFirstTime => {
      if (isFirstTime) {
        this.firstRunService.updateFirstAppRun();
      }
    });
    this.initFromSimulation();
    this.initLanguage();
    this.guidedLoginParams();
    this.analytics.init({
      isDisabled: !!this.mmobileHelper.getCustomConfigData('ANALYTICS_DISABLED'),
      ...analyticsConfig
    });
    this.deeplinks.init();
    this.fixScrollActiveElement();
    this.initSentry();
    await this.biometric.init();
    this.onFirstPageAndIsLogged$().subscribe(() => this.menu.updateMenuState());
    this.opinatorService.init();
    this.onLogout$.subscribe(() => this.ui.closeAlert());
  }

  private async initializeApp() {
    if (!this.platform.is('hybrid')) {
      return;
    }

    if (this.platform.is('android')) {
      LockFontSize.init().catch(() => {
        this.log.i(this, 'Unable to lock font size');
      });
      this.statusBar.styleLightContent();
    }
    if (this.platform.is('ios')) {
      this.statusBar.styleDefault();
    }
    this.updateService.updateData();
    this.badge.clear();
    this.hardwareBackButton.enable();
    this.onAppBlocked$().subscribe(() => this.appBlockGuard.block());
    const isParticular = this.ctx.isAccessTypeParticular;
    this.onAppLoginTimeout$()
      .pipe(switchMap(() => this.auth.dispatch$(new Logout({ softLogout: true }))))
      .subscribe(() => this.nav.goLoginPage(isParticular, { queryParams: { manual: true } }));
    this.fileHelper.cleanTemporaryFiles().catch(_ => {});
    this.splash.hideFromAppComponent();
    await this.notificationsCoreService.init();
    this.trackingService.requestPermission();
  }


  onMenuClick(action: 'OPEN' | 'CLOSE') {
    this.menu.hackMenuCosmetic(action);

    if (action === 'OPEN') {
      of(true)
        .pipe(delay(10))
        .subscribe(() => {
          this.menuBackground = true;
          this.analytics.triggerManualNavigation();
        });
      this.menu.setMenuReady$();
      if (this.auth.getState().isLogged) {
        this.menu.updateUnreadMails();
      }
    } else {
      this.menuRef.onCloseMenu();
    }
  }

  public showZendeskChat(): boolean {
    const ZENDESK_DEKSTOP_FLAG = this.ctx.isDesktop
      ? AllFeatureFlags.ZENDESK_CHAT_WEB
      : AllFeatureFlags.ZENDESK_CHAT_RESPONSIVE;
    const feature = this.ctx.isApp
      ? AllFeatureFlags.ZENDESK_CHAT_APP
      : ZENDESK_DEKSTOP_FLAG;

    return this.auth.getState().isLogged && this.mmobileHelper.checkIsFeatureEnabled(feature);
  }

  public showGenesysChat(): boolean {
    return this.auth.getState()?.isLogged
      && this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.GENESYS_CHAT)
      && !this.auth.checkUserIsCompany();
  }

  private async initLanguage() {
    this.trans.use('es').subscribe({
      error: () => this.log.e(this, `Error using spanish language`)
    });
    this.registerLocalization();
  }

  private fixScrollActiveElement() {
    let disableScroll = false;
    this.keyboard.onKeyboardDidShow().subscribe(_ => {
      if (!disableScroll) {
        // scrollIntoViewOptions not soported on Safari
        // https://docs.w3cub.com/dom/element/scrollintoview/
        this.platform.is('ios')
          ? document.activeElement.scrollIntoView(false)
          : document.activeElement.scrollIntoView({
            block: 'center',
            inline: 'center',
            behavior: 'smooth'
          });
        disableScroll = true;
        this.swipeMenuEnabled = false;
      }
    });
    this.keyboard.onKeyboardWillHide().subscribe(_ => {
      disableScroll = false;
      this.swipeMenuEnabled = true;
    });
  }

  private initSentry() {
    this.sentry.init(
      'https://805438d317be4eaba52ab550952f8750@sentry.io/1820825',
      Environment.config().sentryVersion,
      Environment.config().envName
    );
  }

  private get onLogout$() {
    return this.auth.getState$().pipe(
      distinctUntilKeyChanged('isLogged'),
      filter(({ isLogged }) => !isLogged)
    );
  }

  private onAppResume$() {
    return this.platform.resume.pipe(share());
  }

  private onAppLoginTimeout$() {
    return this.onAppResume$().pipe(
      switchMap(() => this.auth.isAppLoginTimeout()),
      filter(Boolean)
    );
  }

  private onAppBlocked$() {
    const onAppResumeAndReloadMmobile$ = this.onAppResume$().pipe(
      switchMap(() => this.mmobile.reloadConfig())
    );

    return merge(of('onAppInitialization$'), onAppResumeAndReloadMmobile$).pipe(
      filter(() => this.mmobileHelper.isAppBlocked())
    );
  }

  private registerLocalization() {
    registerLocaleData(localeEs, 'es');
    moment.updateLocale('es', {
      weekdaysMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S']
    });
  }

  private onFirstPageAndIsLogged$() {
    return this.ctx.isFirstPage$.pipe(
      filter(Boolean),
      first(),
      map(() => this.auth.getState().isLogged),
      filter(Boolean)
    );
  }


  private initFromSimulation() {
    const params = this.router.getCurrentNavigation().extractedUrl?.queryParams;
    if (params?.co || (params?.usuario && params?.clave)) {
      this.auth.dispatch$(new Logout()).subscribe(
        () => {
          this.nav.newRoot('login', { queryParams: params });
        }
      );
    }
  }

  private setupVehicleAccidentsAccess() {
    this.vehicleAccidentsHelper.getUpdates('vehicle-accidents-entry').subscribe({
      next: _ => {
        this.menu.processActive.next(true);
      }
    });
    this.vehicleAccidentsHelper.getUpdates('vehicle-accidents-exit').subscribe({
      next: _ => {
        this.menu.processActive.next(false);
      }
    });
  }

  private guidedLoginParams() {
    const params = this.router.getCurrentNavigation().extractedUrl?.queryParams;
    const isGuidedLoginAvailable = this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.GUIDED_LOGIN);
    if (params?.hashTC && !this.ctx.isApp && !this.isInsideLoginProccess()) {
      this.auth.dispatch$(new Logout()).subscribe(
        () => {
          this.nav.newRoot('login', { queryParams: params });
        }
      );
    }
    if (params?.srv && isGuidedLoginAvailable) {
      this.guidedLoginHelper.setGuidedLoginParams(
        this.guidedLoginHelper.getGuidedLoginParamsForService(params.srv, params)
      );
    }
  }

  private isInsideLoginProccess() {
    const url = this.router?.getCurrentNavigation()?.extractedUrl?.toString();
    return this.isRegisterProccess(url) || this.isPasswordForgotProccess(url);
  }

  private isRegisterProccess(url: string) {
    return url.includes(RegisterRouting.BASE_PATH);
  }

  private isPasswordForgotProccess(url: string) {
    return url.includes(`${LoginRouting.BASE_PATH}${LoginRouting.FORGOT_PASSWORD}`);
  }

}
